/**
 * World countries and cities for search suggestions.
 *
 * public/data/places.json is built from GeoNames (https://www.geonames.org,
 * CC BY 4.0): every country plus every city with 15,000+ people, sorted by
 * population. It is ~300 KB gzipped, so it is fetched on first use only.
 */

const PLACES_URL = `${process.env.PUBLIC_URL}/data/places.json`;

const POPULAR = [
  ['New York City', 'United States'],
  ['London', 'United Kingdom'],
  ['Paris', 'France'],
  ['Dubai', 'United Arab Emirates'],
  ['Tokyo', 'Japan'],
  ['Las Vegas', 'United States'],
  ['Cancún', 'Mexico'],
  ['Singapore', 'Singapore'],
];

/** Holiday destinations that are islands or regions rather than cities. */
const DESTINATIONS = [
  ['Bali', 'Indonesia'],
  ['Hawaii', 'United States'],
  ['Maui', 'Hawaii, United States'],
  ['Florida Keys', 'Florida, United States'],
  ['Santorini', 'Greece'],
  ['Mykonos', 'Greece'],
  ['Crete', 'Greece'],
  ['Ibiza', 'Spain'],
  ['Mallorca', 'Spain'],
  ['Canary Islands', 'Spain'],
  ['Tuscany', 'Italy'],
  ['Amalfi Coast', 'Italy'],
  ['Sicily', 'Italy'],
  ['Swiss Alps', 'Switzerland'],
  ['Scottish Highlands', 'United Kingdom'],
  ['Goa', 'India'],
  ['Kerala', 'India'],
  ['Kashmir', 'India'],
  ['Phuket', 'Thailand'],
  ['Koh Samui', 'Thailand'],
  ['Langkawi', 'Malaysia'],
  ['Boracay', 'Philippines'],
  ['Zanzibar', 'Tanzania'],
  ['Bora Bora', 'French Polynesia'],
  ['Fiji Islands', 'Fiji'],
  ['Punta Cana', 'Dominican Republic'],
  ['Riviera Maya', 'Mexico'],
  ['Cabo San Lucas', 'Mexico'],
  ['Patagonia', 'Argentina'],
  ['Galápagos Islands', 'Ecuador'],
  ['Banff', 'Alberta, Canada'],
  ['Niagara Falls', 'Ontario, Canada'],
  ['Yellowstone', 'Wyoming, United States'],
  ['Grand Canyon', 'Arizona, United States'],
  ['Napa Valley', 'California, United States'],
];

export const normalizeText = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

let loadPromise = null;

function buildIndex({ countries, regions, cities }) {
  const countryEntries = countries.map(([code, name]) => ({
    kind: 'country',
    name,
    meta: 'Country',
    code,
    label: name,
    key: normalizeText(name),
  }));

  const cityEntries = cities.map(([name, regionIdx, countryIdx]) => {
    const country = countries[countryIdx][1];
    const region = regions[regionIdx];
    const meta = region && region !== name ? `${region}, ${country}` : country;
    return {
      kind: 'city',
      name,
      meta,
      code: countries[countryIdx][0],
      label: `${name}, ${meta}`,
      key: normalizeText(name),
      countryKey: normalizeText(country),
    };
  });

  const destinationEntries = DESTINATIONS.map(([name, meta]) => ({
    kind: 'region',
    name,
    meta,
    label: `${name}, ${meta}`,
    key: normalizeText(name),
  }));

  const popular = POPULAR.map(([city, country]) =>
    cityEntries.find((c) => c.name === city && c.meta.endsWith(country))
  ).filter(Boolean);

  return { countries: countryEntries, destinations: destinationEntries, cities: cityEntries, popular };
}

/** Loads (once) and indexes the world places list. */
export function loadWorldPlaces() {
  if (!loadPromise) {
    loadPromise = fetch(PLACES_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`places.json ${res.status}`);
        return res.json();
      })
      .then(buildIndex)
      .catch((err) => {
        loadPromise = null;
        throw err;
      });
  }
  return loadPromise;
}

/**
 * Best matches for a query: countries, holiday regions and cities whose name starts with it
 * (largest cities first), then cities in a matching country, then cities
 * whose name contains it.
 */
export function searchWorldPlaces(index, query, limit = 8) {
  const q = normalizeText(query);
  if (!index) return [];
  if (!q) return index.popular.slice(0, limit);

  const out = [];
  const seen = new Set();
  const add = (entry) => {
    if (out.length >= limit || seen.has(entry)) return;
    seen.add(entry);
    out.push(entry);
  };

  index.countries.forEach((c) => c.key.startsWith(q) && add(c));
  index.destinations.forEach((d) => d.key.startsWith(q) && add(d));
  for (const c of index.cities) {
    if (out.length >= limit) break;
    if (c.key.startsWith(q)) add(c);
  }
  if (q.length >= 3) {
    for (const c of index.cities) {
      if (out.length >= limit) break;
      if (c.countryKey.startsWith(q)) add(c);
    }
    for (const c of index.cities) {
      if (out.length >= limit) break;
      if (c.key.includes(q)) add(c);
    }
  }
  return out;
}
